import styled from '@emotion/styled';
import TestSelectors from '@racemap/utilities/consts/test_selectors';
import { isEmptyString } from '@racemap/utilities/functions/utils';
import { Alert, Button, Flex, Result, Space, Typography } from 'antd';
import type { ResultProps, ResultStatusType } from 'antd/es/result';
import React, { type FC, useEffect, useState } from 'react';
import {
  isRouteErrorResponse,
  type NavigateFunction,
  useLocation,
  useNavigate,
  useRouteError,
} from 'react-router-dom';
import shortHash from 'shorthash2';
import { isNewBuildAvailable } from 'src/lib/buildState';
import { type ExtendedError, UnauthorizedError } from 'src/lib/Errors';
import { sendErrorEvent, trackEvent } from 'src/lib/googleAnalytics';
import { clearSourceMapCache, mapStackTrace, parseStackLine } from './stackUtils';

const { Text, Link } = Typography;

export const ErrorPage: FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  console.error(error);

  useEffect(() => {
    sendErrorEvent(error, true);
  }, [error]);

  useEffect(() => {
    // Clear source map cache on unmount to free memory
    return clearSourceMapCache();
  }, []);

  return <ErrorResult error={error} navigate={navigate} />;
};

const ErrorResult: FC<{ error: unknown; navigate: NavigateFunction }> = ({ error, navigate }) => {
  const { title, ...props } = getResultProps(error, navigate);

  return (
    <Result
      {...props}
      className={TestSelectors.errorPage.container.toString()}
      title={<span className={TestSelectors.errorPage.title.toString()}>{title}</span>}
    />
  );
};

const getResultProps = (error: unknown, navigate: NavigateFunction): ResultProps => {
  if (isWebGLSupportError(error)) {
    return {
      status: 'error',
      title: 'Missing WebGL Support',
      subTitle:
        'Your browser does not support WebGL, which is required to run this application. Please use a different browser or update your current browser to the latest version.',
    };
  }

  if (isRouteErrorResponse(error)) {
    return {
      status: error.status as ResultStatusType,
      title: error.statusText,
      subTitle: error.data?.message || subTitles[error.status as ResultStatusType],
      extra: getExtra(error.status as ResultStatusType, navigate),
    };
  }
  if (error instanceof UnauthorizedError) {
    return {
      status: 403,
      title: error.name,
      subTitle: error.message,
      extra: getExtra(403, navigate),
    };
  }
  if (error instanceof Error) {
    return {
      status: 'error',
      title: error.name,
      subTitle: error.message,
      extra: getExtra('error', navigate, error),
    };
  }

  return {
    status: 'error',
    title: 'An error occurred',
    subTitle: 'Unknown error. Please try again later.',
    extra: getExtra('error', navigate),
  };
};

/**
 * Only rendered once the build watcher has actually seen a newer version, so it never tells
 * someone to reload when that would not help.
 */
const OutdatedBuildAlert: FC = () => {
  if (!isNewBuildAvailable()) return null;

  return (
    <Alert
      type="warning"
      description={
        <Flex vertical gap={10} align="flex-start">
          <Text strong>New version available</Text>
          <Text style={{ textAlign: 'start' }}>
            A newer version has been released since this page was opened.
            <br />
            Reloading may well fix this error.
          </Text>
          <Flex justify="center" style={{ width: '100%', padding: 10 }}>
            <Button
              type="primary"
              size="small"
              style={{ width: 200 }}
              onClick={() => {
                trackEvent('build_update_reload', { reason: 'error_page_click' });
                window.location.reload();
              }}
            >
              Reload
            </Button>
          </Flex>
        </Flex>
      }
    />
  );
};

const subTitles: Record<ResultStatusType, string> = {
  403: 'You are not authorized to view this page',
  404: 'The requested page could not be found',
  500: 'An unexpected error occurred',
  info: 'There was an informational message',
  success: 'The operation was successful',
  error: 'An error occurred',
  warning: 'There was a warning',
};

const getExtra = (errorStatus: ResultStatusType, navigate: NavigateFunction, error?: Error) => {
  const backHome = (
    <Button type="primary" onClick={() => navigate('/admin')}>
      Back Home
    </Button>
  );

  const isStackError = error != null && !isStatusError(errorStatus);

  // One column for everything antd renders below the subtitle. Sized to its widest child, so
  // the outdated-build alert ends up exactly as wide as the stack panel next to it.
  return (
    <Flex justify="center">
      <ExtraColumn>
        <OutdatedBuildAlert />
        {isStackError ? (
          <Alert type="error" title={<ErrorMessage error={error} />} />
        ) : (
          // Wrapped, not stretched: the column can be alert-wide and a full-width button
          // would look absurd.
          <Flex justify="center">{backHome}</Flex>
        )}
      </ExtraColumn>
    </Flex>
  );
};

const isStatusError = (errorStatus: ResultStatusType) =>
  errorStatus === 403 || errorStatus === 404 || errorStatus === 500;

const ExtraColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    width: fit-content;
    max-width: 100%;
`;

const ErrorMessage: FC<{ error: Error }> = ({ error }) => {
  const { message, stack } = error;
  const metadata = 'metadata' in error ? (error as ExtendedError).metadata : undefined;
  const [mappedStack, setMappedStack] = useState<Array<string>>([]);
  const location = useLocation();

  useEffect(() => {
    if (stack) {
      mapStackTrace(stack).then(setMappedStack);
    }
  }, [stack]);

  const stackLines = mappedStack.length > 0 ? mappedStack : stack?.split('\n') || [];
  const subject = encodeURI('Frontend Error');
  const body = encodeURI(getTextVersionOfError(error, mappedStack.join('\n')));

  return (
    <StackBox vertical gap={15} align="flex-start">
      <InfoText>
        We apologize for the inconvenience caused by the software issue and try to fix that problem
        as fast as possible. You can help us, if you send us the content of that panel to our
        support mail{' '}
        <Link href={`mailto:support@racemap.com?subject=${subject}&body=${body}`}>
          support@racemap.com
        </Link>
        .
      </InfoText>
      <ErrorMessageContainer>
        <Space>
          <Text strong>Message:</Text> <Text>{message}</Text>
        </Space>
        <Space>
          <Text strong>Version:</Text> <Text>{window.__BUILD_VERSION__ || 'unknown'}</Text>
        </Space>
        <Space>
          <Text strong>Current URL:</Text> <Text>{window.location.origin + location.pathname}</Text>
        </Space>
        <Space>
          <Text strong>Occurred At:</Text> <Text>{new Date().toISOString()}</Text>
        </Space>

        <ErrorMetadata metadata={metadata} />
      </ErrorMessageContainer>
      <Text strong>Error stack:</Text>
      <StackEntries>
        {stackLines.map((line, index) => {
          if (isEmptyString(line)) return null;

          const parsed = parseStackLine(line);
          if (!parsed) return null;

          return (
            <React.Fragment key={shortHash(line)}>
              <LineNumber>{index}</LineNumber>
              <ErrorMethod strong>{parsed.method}</ErrorMethod>
              <Text>{parsed.file}</Text>
            </React.Fragment>
          );
        })}
      </StackEntries>
    </StackBox>
  );
};

const ErrorMetadata: FC<{ metadata?: Record<string, any> }> = ({ metadata }) => {
  if (!metadata) return null;

  return (
    <Space orientation="vertical" style={{ width: '100%' }}>
      <Text strong>Metadata:</Text>
      {Object.entries(metadata).map(([key, value]) => {
        const stringValue = String(JSON.stringify(value));
        const displayValue =
          stringValue.length > 200 ? `${stringValue.substring(0, 200)}...` : stringValue;
        return (
          <Space key={key}>
            <Text strong>{key}:</Text> <Text>{displayValue}</Text>
          </Space>
        );
      })}
    </Space>
  );
};

const getTextVersionOfError = (error: Error, mappedStack?: string) => {
  const { message, stack } = error;
  const now = new Date().toISOString();
  const metadata = 'metadata' in error ? (error as ExtendedError).metadata : undefined;

  return `
    Message: ${message}
    Version: ${window.__BUILD_VERSION__ || 'unknown'}
    Current URL: ${window.location.origin + window.location.pathname}
    Occurred at: ${now}
    ${getMetadataString(metadata)}

    Error stack:
    ${mappedStack || stack}
  `;
};

const getMetadataString = (metadata?: Record<string, any>) => {
  if (!metadata) return '';

  return Object.entries(metadata)
    .map(([key, value]) => `    ${key}: ${JSON.stringify(value)}`)
    .join('\n');
};

const isWebGLSupportError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return error.message.includes('Framebuffer error');
  }
  return false;
};

const StackBox = styled(Flex)`
    text-align: start;
    max-height: 50vh;
    overflow: auto;
    padding: 10px;
    white-space: nowrap;
    max-width: 900px;
`;

const StackEntries = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: 3ch 40ch 1fr;
    gap: 5px;
    overflow: auto;
    padding: 10px;
    white-space: nowrap;
    font-family: monospace;
    text-align: start;
    overflow: auto;
`;

const ErrorMessageContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const LineNumber = styled(Text)`
    font-family: monospace;
    white-space: nowrap;
    overflow: auto;
`;

const ErrorMethod = styled(Text)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const InfoText = styled(Text)`
    white-space: normal;
    overflow: unset;
`;
